import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";

// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Divider,
  Button,
  Dialog,
  IconButton,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import AppConfig from "appConfig";
import usePageTitle from "app/pageTitle";
import MDButton from "theme/components/MDButton";

function WaccNumberPage() {
  const pageTitle = "WhatsApp Number";
  usePageTitle(pageTitle);

  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [number, setNumber] = useState(null);
  const [performance, setPerformance] = useState({
    contacts: 0,
    conversations: 0,
  });
  const [incomingMessages, setIncomingMessages] = useState([]);
  const [previewMessage, setPreviewMessage] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get(`agents/wacc/numbers/${ref}/fetch.json`)
        .then((result) => {
          setShowLoading(false);

          if (result.data.status === "success") {
            setNumber(result.data.data.number);
            setPerformance(result.data.data.performance);
            setIncomingMessages(result.data.data.incoming_messages || []);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`,
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [ref]);

  const getMessagePreview = (messagePayload) => {
    try {
      const payload =
        typeof messagePayload === "string"
          ? JSON.parse(messagePayload.replace(/'/g, '"'))
          : messagePayload;

      if (payload?.type === "text") {
        return payload.text?.body || "Text message";
      }

      if (payload?.type === "image") {
        return payload.image?.caption || "Image";
      }

      if (payload?.type === "video") {
        return payload.video?.caption || "Video";
      }

      if (payload?.type === "audio") {
        return "Audio message";
      }

      if (payload?.type === "document") {
        return payload.document?.filename || "Document";
      }

      if (payload?.type === "sticker") {
        return "Sticker";
      }

      if (payload?.type === "location") {
        return "Location";
      }

      if (payload?.type === "contacts") {
        return "Contact";
      }

      if (payload?.type === "button") {
        return payload.button?.text || "Button response";
      }

      if (payload?.type === "interactive") {
        return (
          payload.interactive?.button_reply?.title ||
          payload.interactive?.list_reply?.title ||
          "Interactive response"
        );
      }

      return `${payload?.type || "Message"} message`;
    } catch (error) {
      return "Message preview unavailable";
    }
  };

  const handlePreviewMessage = (message) => {
    setPreviewMessage(message);
    setPreviewDialogOpen(true);
  };

  const handleAcceptMessage = async (message) => {
    setShowLoading(true);
    await axios
      .post("agents/wacc/numbers/incoming-messages/accept.json", {
        reference: message.reference,
      })
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          //   setIncomingMessages((current) =>
          //     current.filter((item) => item.reference !== message.reference),
          //   );

          //   setPerformance((current) => ({
          //     ...current,
          //     conversations: current.conversations + 1,
          //   }));
          navigate(`/wacc/numbers/${ref}/conversations`);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine
              ? ""
              : "Make sure you have an active internet connection"
          }`,
        );
        setOpen(true);
      });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog
        open={previewDialogOpen}
        onClose={() => {
          setPreviewDialogOpen(false);
          setPreviewMessage(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <MDBox p={3}>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <MDBox>
              <MDTypography variant="h6">
                {previewMessage?.contact?.name}
              </MDTypography>

              <MDTypography variant="caption">
                {previewMessage?.contact?.phone_number}
              </MDTypography>
            </MDBox>
          </MDBox>
          <MDBox mb={1}>
            <MDBox
              mt={1}
              p={2}
              sx={{
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
              }}
            >
              <MDTypography variant="body2">
                {previewMessage &&
                  getMessagePreview(previewMessage.message_payload)}
              </MDTypography>
            </MDBox>
          </MDBox>

          <MDBox display="flex" justifyContent="flex-end">
            <Button
              color="dark"
              size="small"
              onClick={() => {
                setPreviewDialogOpen(false);
                setPreviewMessage(null);
              }}
            >
              Close
            </Button>
          </MDBox>
        </MDBox>
      </Dialog>

      <MDBox mt={1} mb={5}>
        <MDBox mb={1} display="flex" justifyContent="space-between">
          <Button
            color="dark"
            size="small"
            startIcon={<Icon>west</Icon>}
            onClick={() => {
              navigate(`/wacc`);
            }}
          >
            Back
          </Button>

          <MDBox display="flex" gap={1}></MDBox>
        </MDBox>

        {number && (
          <MDBox mb={3}>
            <MDBox>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={7} sx={{ display: "flex" }}>
                    <Card sx={{ width: "100%" }}>
                      <MDBox p={2}>
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          px={1}
                        >
                          <MDBox>
                            <MDTypography
                              variant="caption"
                              fontWeight="regular"
                            >
                              {number.account.name}
                            </MDTypography>

                            <MDTypography variant="h6" fontSize={18}>
                              {number.business}
                            </MDTypography>

                            <MDTypography
                              variant="caption"
                              fontWeight="regular"
                            >
                              {number.phone_number}
                            </MDTypography>
                          </MDBox>

                          <MDBox>
                            <img
                              src={`${AppConfig.api_media_url}${number.account.icon}`}
                              width="40px"
                            />
                          </MDBox>
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={5} sx={{ display: "flex" }}>
                    <Card
                      onClick={() => {
                        navigate(`/wacc/numbers/${ref}/conversations`);
                      }}
                      sx={{
                        cursor: "pointer",
                        width: "100%",
                      }}
                    >
                      <MDBox p={2}>
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <MDBox>
                            <MDTypography
                              variant="caption"
                              fontWeight="regular"
                            >
                              Conversations
                            </MDTypography>

                            <MDTypography variant="h4">
                              {performance.conversations}
                            </MDTypography>
                          </MDBox>

                          <Icon>forum</Icon>
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
              </Grid>
            </MDBox>

            <MDBox mt={2}>
              <Card>
                <MDBox p={2}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <MDBox>
                      <MDTypography variant="h6" fontSize={16}>
                        Awaiting Messages
                      </MDTypography>

                      <MDTypography variant="caption" fontWeight="regular">
                        Incoming messages waiting to be accepted.
                      </MDTypography>
                    </MDBox>

                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDTypography variant="h5">
                        {incomingMessages.length}
                      </MDTypography>

                      <Icon>inbox</Icon>
                    </MDBox>
                  </MDBox>

                  <Divider />

                  {incomingMessages.length === 0 ? (
                    <MDBox py={3} textAlign="center">
                      <MDTypography
                        variant="body2"
                        color="text"
                        fontWeight="regular"
                      >
                        No messages awaiting acceptance.
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <MDBox>
                      {incomingMessages.map((message) => (
                        <MDBox
                          key={message.reference}
                          py={1.5}
                          sx={{
                            borderBottom: "1px solid #eeeeee",
                          }}
                        >
                          <MDBox
                            display="flex"
                            alignItems="flex-start"
                            gap={1.5}
                          >
                            {/* <Icon sx={{ mt: 0.5 }}>person</Icon> */}

                            <MDBox minWidth={0} flex={1}>
                              <MDTypography
                                variant="button"
                                fontWeight="medium"
                                display="block"
                                sx={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {message.contact.name}
                              </MDTypography>

                              <MDTypography
                                variant="caption"
                                fontWeight="regular"
                                display="block"
                              >
                                {message.contact.phone_number}
                              </MDTypography>

                              <MDTypography
                                variant="body2"
                                display="block"
                                sx={{
                                  mt: 0.5,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {getMessagePreview(message.message_payload)}
                              </MDTypography>
                            </MDBox>
                          </MDBox>

                          <MDBox
                            mt={1}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            gap={1}
                            sx={{
                              flexWrap: "wrap",
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              fontWeight="regular"
                              sx={{
                                whiteSpace: "nowrap",
                              }}
                            >
                              {message.created}
                            </MDTypography>

                            <MDBox
                              display="flex"
                              gap={1}
                              sx={{
                                flexWrap: "wrap",
                              }}
                            >
                              <MDButton
                                color="dark"
                                size="small"
                                variant="outlined"
                                onClick={() => handlePreviewMessage(message)}
                              >
                                Preview
                              </MDButton>

                              <MDButton
                                color="dark"
                                size="small"
                                variant="contained"
                                onClick={() => handleAcceptMessage(message)}
                              >
                                Accept
                              </MDButton>
                            </MDBox>
                          </MDBox>
                        </MDBox>
                      ))}
                    </MDBox>
                  )}
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default WaccNumberPage;
